// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { RECURRING_FREQUENCY_UNITS, numberToDayOfWeek, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// eslint-disable-next-line no-shadow
export enum FrequencyLabel {
  EVERY_X_WEEKS = 'Every N weeks',
  EVERY_X_DAY_OF_WEEK = 'Every Nth day of the week',
  EVERY_X_DAYS = 'Every N days',
  EVERY_NTH_WEEKDAY_OF_MONTH = 'Every Nth weekday of the month',
}
export const daysOfTheWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const EVERY_X_WEEKS = 'Every N weeks';
export const EVERY_X_DAY_OF_WEEK = 'Every Nth day of the week';
export const EVERY_X_DAYS = 'Every N days';
export const EVERY_NTH_WEEKDAY_OF_MONTH = 'Every Nth weekday of the month';
export const orderFrequencyOptions = [EVERY_X_WEEKS, EVERY_X_DAY_OF_WEEK, EVERY_NTH_WEEKDAY_OF_MONTH];
export const taskFrequencyOptions = [EVERY_X_WEEKS, EVERY_X_DAY_OF_WEEK, EVERY_X_DAYS, EVERY_NTH_WEEKDAY_OF_MONTH];
export const plantingFrequencyOptions = [EVERY_X_WEEKS, EVERY_X_DAY_OF_WEEK, EVERY_NTH_WEEKDAY_OF_MONTH];
export const DEFAULT_FREQUENCY_UNIT = EVERY_X_DAY_OF_WEEK;
export const frequencyLabelToInput = (label: FrequencyLabel) => {
  switch (label) {
    case FrequencyLabel.EVERY_X_DAY_OF_WEEK:
      return RECURRING_FREQUENCY_UNITS.DAY_OF_WEEK;
    case FrequencyLabel.EVERY_X_WEEKS:
      return RECURRING_FREQUENCY_UNITS.WEEKS;
    case FrequencyLabel.EVERY_X_DAYS:
      return RECURRING_FREQUENCY_UNITS.DAYS;
    case FrequencyLabel.EVERY_NTH_WEEKDAY_OF_MONTH:
      return RECURRING_FREQUENCY_UNITS.NTH_WEEKDAY_OF_MONTH;
    default:
      utils.assertNever(label);
      return 'INVALID_CLIENT_SIDE_FREQUENCY_LABEL';
  }
};
export const frequencyTypeToLabel = (type: string): FrequencyLabel => {
  switch (type) {
    case RECURRING_FREQUENCY_UNITS.WEEKS:
      return FrequencyLabel.EVERY_X_WEEKS;
    case RECURRING_FREQUENCY_UNITS.DAY_OF_WEEK:
      return FrequencyLabel.EVERY_X_DAY_OF_WEEK;
    case RECURRING_FREQUENCY_UNITS.DAYS:
      return FrequencyLabel.EVERY_X_DAYS;
    case RECURRING_FREQUENCY_UNITS.NTH_WEEKDAY_OF_MONTH:
      return FrequencyLabel.EVERY_NTH_WEEKDAY_OF_MONTH;
    // case RECURRING_FREQUENCY_UNITS.DAY_OF_MONTH:
    default:
      // utils.assertNever(type);
      // return 'INVALID_CLIENT_SIDE_FREQUENCY_TYPE';
      throw new Error('INVALID_CLIENT_SIDE_FREQUENCY_TYPE');
  }
};

interface SetFrequencyStatesInput {
  readonly recurringOptions: {
    frequencyGroups: {
      frequencyType: string;
      frequencies: number[];
    }[];
  };
  readonly timezone: string;
  readonly startTime: number;
  readonly endTime?: number | null;
  readonly setFrequencyUnit: (frequencyUnit: FrequencyLabel) => void;
  readonly setFrequency: (frequency: string) => void;
  readonly setDayOfWeek: (dayOfWeek: string) => void;
  readonly setOccurence: (occurence: string) => void;
  readonly setRecursForever: (recursForever: boolean) => void;
  readonly setStartDate: (startDate: dayjs.Dayjs) => void;
  readonly setEndDate: (endDate: dayjs.Dayjs) => void;
  // Used when editing an existing recurring order/planting times
  readonly setExistingFrequencyUnit?: (existingFrequencyUnit: FrequencyLabel) => void;
  readonly setExistingFrequency?: (existingFrequency: string) => void;
  readonly setExistingDayOfWeek?: (existingDayOfWeek: string) => void;
  readonly setExistingOccurence?: (existingOccurence: string) => void;
  readonly setExistingRecursForever?: (existingRecursForever: boolean) => void;
  readonly setExistingStartDate?: (existingStartDate: dayjs.Dayjs) => void;
  readonly setExistingEndDate?: (existingEndDate: dayjs.Dayjs) => void;
}

export function setFrequencyStates({
  recurringOptions,
  timezone,
  startTime,
  endTime,
  setFrequencyUnit,
  setFrequency,
  setDayOfWeek,
  setOccurence,
  setRecursForever,
  setStartDate,
  setEndDate,
  setExistingFrequencyUnit,
  setExistingFrequency,
  setExistingDayOfWeek,
  setExistingOccurence,
  setExistingRecursForever,
  setExistingStartDate,
  setExistingEndDate,
}: SetFrequencyStatesInput) {
  setRecursForever(endTime == null);
  setExistingRecursForever?.(endTime == null);
  setStartDate(dayjs.unix(startTime).tz(timezone));
  setExistingStartDate?.(dayjs.unix(startTime).tz(timezone));
  if (endTime != null) {
    setEndDate(dayjs.unix(endTime).tz(timezone));
    setExistingEndDate?.(dayjs.unix(endTime).tz(timezone));
  }

  const frequencyType = recurringOptions.frequencyGroups[0].frequencyType;
  setFrequencyUnit(frequencyTypeToLabel(frequencyType));
  setExistingFrequencyUnit?.(frequencyTypeToLabel(frequencyType));
  const frequencies = recurringOptions.frequencyGroups[0].frequencies;

  if (frequencyType === RECURRING_FREQUENCY_UNITS.DAY_OF_WEEK) {
    setDayOfWeek(numberToDayOfWeek(frequencies[0]));
    setExistingDayOfWeek?.(numberToDayOfWeek(frequencies[0]));
  } else if (frequencyType === RECURRING_FREQUENCY_UNITS.NTH_WEEKDAY_OF_MONTH) {
    const [targetWeekday, nthOccurrence] = frequencies;
    setDayOfWeek(numberToDayOfWeek(targetWeekday));
    setExistingDayOfWeek?.(numberToDayOfWeek(targetWeekday));
    setOccurence(nthOccurrence.toString());
    setExistingOccurence?.(nthOccurrence.toString());
  } else {
    // EVERY_X_WEEKS || EVERY_X_DAYS
    setFrequency(frequencies[0].toString());
    setExistingFrequency?.(frequencies[0].toString());
  }
}
